/**
 * VirtualServerTabs - Tab navigation for filtering tools by virtual server
 * 
 * Provides horizontal tab navigation that allows users to:
 * - View all tools from the parent service
 * - Filter to a specific virtual server's tool bundle
 * - See tool counts per virtual server
 * 
 * Blue Steel Compliant:
 * - Zero hardcoded colors (CSS variables)
 * - Smooth tab transitions
 * - Tooltips with VS descriptions
 * - Selection badge on active tab
 * 
 * @example
 * ```tsx
 * <VirtualServerTabs
 *   virtualServers={virtualServers}
 *   activeVsId={selectedVsId}
 *   onSelect={setSelectedVsId}
 *   totalToolCount={totalTools}
 * />
 * ```
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import type { VirtualServer } from './types';

export interface VirtualServerTabsProps {
  virtualServers: VirtualServer[];
  activeVsId: string | null;  // null = "All" tab
  onSelect: (vsId: string | null) => void;
  totalToolCount?: number;
  loading?: boolean;
  showToolCounts?: boolean;
}

export const VirtualServerTabs: React.FC<VirtualServerTabsProps> = ({
  virtualServers,
  activeVsId,
  onSelect,
  totalToolCount,
  loading = false,
  showToolCounts = true,
}) => {
  const [hoveredTab, setHoveredTab] = useState<string | null>(null);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [tooltipContent, setTooltipContent] = useState<{ name: string; description: string } | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const tooltipTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup tooltip timeout on unmount
  useEffect(() => {
    return () => {
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
      }
    };
  }, []);

  const handleMouseEnter = useCallback((vs: VirtualServer | null, e: React.MouseEvent) => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setTooltipPosition({ x: rect.left + rect.width / 2, y: rect.bottom + 8 });
    
    if (vs) {
      setTooltipContent({ name: vs.name, description: vs.description });
      setHoveredTab(vs.id);
    } else {
      setTooltipContent({ name: 'All Tools', description: 'Show all tools from the service' });
      setHoveredTab('__all__');
    }

    // Delay showing tooltip
    tooltipTimeoutRef.current = setTimeout(() => {
      setTooltipVisible(true);
    }, 400);
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
    }
    setTooltipVisible(false);
    setHoveredTab(null);
  }, []);

  const handleKeyDown = useCallback((vsId: string | null, e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect(vsId);
    }
  }, [onSelect]);

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        gap: 8,
        padding: '8px 0',
        marginBottom: 16,
      }}>
        {[1, 2, 3].map(i => (
          <div
            key={i}
            style={{
              width: 100,
              height: 32,
              borderRadius: 8,
              background: 'var(--skeleton-bg)',
              animation: 'shimmer 1.5s infinite',
            }}
          />
        ))}
      </div>
    );
  }

  if (virtualServers.length === 0) {
    return null;  // No tabs needed if no virtual servers
  }

  return (
    <div style={{ position: 'relative' }}>
      {/* Tabs Container */}
      <div
        role="tablist"
        aria-label="Filter tools by virtual server"
        style={{
          display: 'flex',
          gap: 6,
          padding: '8px 0',
          marginBottom: 16,
          overflowX: 'auto',
          scrollbarWidth: 'thin',
        }}
      >
        {/* "All" Tab */}
        <button
          role="tab"
          aria-selected={activeVsId === null}
          tabIndex={activeVsId === null ? 0 : -1}
          onClick={() => onSelect(null)}
          onKeyDown={(e) => handleKeyDown(null, e)}
          onMouseEnter={(e) => handleMouseEnter(null, e)}
          onMouseLeave={handleMouseLeave}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '8px 14px',
            borderRadius: 8,
            border: 'none',
            background: activeVsId === null
              ? 'var(--main-accent)'
              : hoveredTab === '__all__'
              ? 'color-mix(in srgb, var(--main-accent) 15%, transparent)'
              : 'var(--card-bg)',
            color: activeVsId === null ? 'white' : 'var(--main-text)',
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            whiteSpace: 'nowrap',
            boxShadow: activeVsId === null
              ? '0 2px 8px color-mix(in srgb, var(--main-accent) 35%, transparent)'
              : 'none',
          }}
        >
          <span>📋</span>
          <span>All</span>
          {showToolCounts && totalToolCount !== undefined && (
            <span style={{
              padding: '1px 6px',
              borderRadius: 10,
              fontSize: 10,
              fontWeight: 700,
              background: activeVsId === null
                ? 'color-mix(in srgb, white 25%, transparent)'
                : 'color-mix(in srgb, var(--main-text) 10%, transparent)',
            }}>
              {totalToolCount}
            </span>
          )}
        </button>

        {/* Virtual Server Tabs */}
        {virtualServers.map(vs => {
          const isActive = activeVsId === vs.id;
          const isHovered = hoveredTab === vs.id;
          const icon = vs.icon || '📦';

          return (
            <button
              key={vs.id}
              role="tab"
              aria-selected={isActive}
              tabIndex={isActive ? 0 : -1}
              onClick={() => onSelect(vs.id)}
              onKeyDown={(e) => handleKeyDown(vs.id, e)}
              onMouseEnter={(e) => handleMouseEnter(vs, e)}
              onMouseLeave={handleMouseLeave}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '8px 14px',
                borderRadius: 8,
                border: 'none',
                background: isActive
                  ? 'var(--main-accent)'
                  : isHovered
                  ? 'color-mix(in srgb, var(--main-accent) 15%, transparent)'
                  : 'var(--card-bg)',
                color: isActive ? 'white' : 'var(--main-text)',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                whiteSpace: 'nowrap',
                boxShadow: isActive
                  ? '0 2px 8px color-mix(in srgb, var(--main-accent) 35%, transparent)'
                  : 'none',
              }}
            >
              <span>{icon}</span>
              <span>{vs.name}</span>
              {showToolCounts && (
                <span style={{
                  padding: '1px 6px',
                  borderRadius: 10,
                  fontSize: 10,
                  fontWeight: 700,
                  background: isActive
                    ? 'color-mix(in srgb, white 25%, transparent)'
                    : 'color-mix(in srgb, var(--main-text) 10%, transparent)',
                }}>
                  {vs.tool_count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Tooltip */}
      {tooltipVisible && tooltipContent && (
        <div
          role="tooltip"
          style={{
            position: 'fixed',
            left: tooltipPosition.x,
            top: tooltipPosition.y,
            transform: 'translateX(-50%)',
            padding: '8px 12px',
            background: 'var(--tooltip-bg, var(--card-bg))',
            border: '1px solid var(--border-color)',
            borderRadius: 8,
            boxShadow: 'var(--shadow-lg)',
            zIndex: 1000,
            maxWidth: 250,
            animation: 'tooltipFadeIn 0.15s ease',
          }}
        >
          <div style={{
            fontSize: 12,
            fontWeight: 600,
            color: 'var(--main-text)',
            marginBottom: 4,
          }}>
            {tooltipContent.name}
          </div>
          <div style={{
            fontSize: 11,
            color: 'var(--main-text-secondary)',
            lineHeight: 1.4,
          }}>
            {tooltipContent.description}
          </div>
          {/* Arrow */}
          <div style={{
            position: 'absolute',
            top: -6,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 0,
            height: 0,
            borderLeft: '6px solid transparent',
            borderRight: '6px solid transparent',
            borderBottom: '6px solid var(--border-color)',
          }} />
        </div>
      )}

      {/* Keyframes */}
      <style>{`
        @keyframes shimmer {
          0% { opacity: 0.5; }
          50% { opacity: 0.8; }
          100% { opacity: 0.5; }
        }
        @keyframes tooltipFadeIn {
          from {
            opacity: 0;
            transform: translateX(-50%) translateY(-4px);
          }
          to {
            opacity: 1;
            transform: translateX(-50%) translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

